import React, { useCallback } from 'react';

import Dropdown from 'react-dropdown';
import ReCAPTCHA from 'react-google-recaptcha';
import { useTranslation } from 'react-i18next';

import { RECAPTCHA_SITE_KEY, IS_INDIAN_EXCHANGE } from 'constants/constants';
import { noop } from 'helpers/utils';
import i18n from 'i18n/config';
import { selectUserCountry } from 'selectors/otherSelectors';
import { useAppSelector } from 'storeHooks';
import { AngleDown, AngleUp, CrossIcon } from 'styles/assets/icons';
import Box from 'UIKit/Box';
import DeltaButton from 'UIKit/DeltaButton/DeltaButton';
import IconButton from 'UIKit/IconButton';
import NumberInput from 'UIKit/NumberInput';

import useEnterPhone, {
  EnterPhoneActionTypes,
} from '../hooks/useEnterPhone/useEnterPhone';
import {
  CODES_OF_MULTIPLE_COUNTRIES,
  COUNTRIES_WITH_MULTIPLE_COUNTRY_CODE,
  MULTIPLE_COUNTRY_CODES_COUNTRIES,
} from '../PhoneVerification.constants';
import { SetScreenType } from '../PhoneVerification.types';
import styles from './enterPhone.module.scss';

interface EnterPhoneNumberProps {
  phone: string;
  closeModal: () => void;
  onPhoneChange: (val: string) => void;
  onCountryCodeChange: (val: string) => void;
  countryCode: string;
  formattedCountryCode: string;
  setScreen: SetScreenType;
  onGetOtpResponse?: (success: boolean, error: unknown) => void;
}

const phoneInputPlaceholder = (
  IS_INDIAN_EXCHANGE
    ? i18n.t('account:profile.enterIndianPhoneNo')
    : i18n.t('account:profile.enterPhoneNo')
) as string;

const EnterPhone = ({
  closeModal,
  countryCode,
  onCountryCodeChange,
  onPhoneChange,
  phone,
  formattedCountryCode,
  setScreen,
  onGetOtpResponse,
}: EnterPhoneNumberProps) => {
  const { t } = useTranslation(['account', 'errors']);

  const country = useAppSelector(selectUserCountry);

  const doesHaveMultipleCodes = COUNTRIES_WITH_MULTIPLE_COUNTRY_CODE.includes(country);

  const onGetOtpSuccess = useCallback(() => {
    setScreen('confirmOtp');
    onGetOtpResponse?.(true, null);
  }, [setScreen, onGetOtpResponse]);

  const onGetOtpError = useCallback(
    (error: unknown) => {
      onGetOtpResponse?.(false, error);
    },
    [onGetOtpResponse]
  );

  const {
    apiStatus,
    errorMessage,
    recaptchaRef,
    verifyPhoneNumber,
    isPhoneNoValid,
    enterPhoneDispatch,
  } = useEnterPhone({
    phone,
    formattedCountryCode,
    country,
    onSuccessCallBack: onGetOtpSuccess,
    onErrorCallBack: onGetOtpError,
  });

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onPhoneChange(event.target.value);
    enterPhoneDispatch({
      type: EnterPhoneActionTypes.ERROR_UPDATE,
      payload: {
        message: '',
      },
    });
  };

  return (
    <Box className={styles.main}>
      <Box flex="inline" textColor="primary" className={styles.headerContainer}>
        <Box as="h4" autoMargin="both">
          {t('account:profile.phoneVerification')}
        </Box>
        <IconButton
          data-testid="closeEnterPhone"
          icon={<CrossIcon />}
          onClick={closeModal}
          onKeyDown={noop}
        />
      </Box>
      <Box flex="flex" flexDir="column" className={styles.body}>
        <Box flex="inline" textColor="secondary" gap={16}>
          <Dropdown
            options={
              doesHaveMultipleCodes
                ? CODES_OF_MULTIPLE_COUNTRIES[country as MULTIPLE_COUNTRY_CODES_COUNTRIES]
                : []
            }
            disabled={!doesHaveMultipleCodes}
            className={styles.countryCodeDropdown}
            data-testid="countryDropdown"
            controlClassName={
              doesHaveMultipleCodes
                ? styles.countryCodeDropdownControl
                : styles.countryCodeDropdownControlForIndia
            }
            value={countryCode}
            arrowClosed={<AngleDown />}
            arrowOpen={<AngleUp />}
            placeholder={countryCode}
            placeholderClassName={styles.countryCodeDropdownPlaceHolder}
            menuClassName={styles.countryCodeDropdownMenu}
            onChange={arg => {
              onCountryCodeChange(arg.value);
            }}
          />
          <NumberInput
            placeholder={phoneInputPlaceholder}
            data-testid="phoneInput"
            value={phone}
            numberType="natural"
            className={styles.phoneInput}
            onChange={handleInputChange}
            autoFocus
          />
        </Box>

        <Box
          renderWhen={Boolean(phone && !isPhoneNoValid && !errorMessage)}
          fontSize="sm2"
          textColor="negative"
        >
          {t('errors:user.invalid_phone_number')}
        </Box>
        <Box renderWhen={Boolean(errorMessage)} textColor="negative" fontSize="sm2">
          {errorMessage}
        </Box>
        <DeltaButton
          data-testid="enterPhoneNumber"
          text={t('account:profile.phoneNoVerification')}
          onClick={verifyPhoneNumber}
          disabled={!isPhoneNoValid}
          loading={apiStatus === 'loading'}
        />
      </Box>
      <ReCAPTCHA
        sitekey={RECAPTCHA_SITE_KEY}
        ref={recaptchaRef}
        theme="dark"
        size="invisible"
        className={styles.recaptcha}
      />{' '}
    </Box>
  );
};

export default EnterPhone;
